import { useCallback, useEffect } from "react";

import { useObjectMemo } from "byzantine";

import { useCurrentUser } from "../../../components/contexts/CurrentUserContext";
import { useSidebarContainer } from "../sidebar/sidebar.hooks";

import { useOnboardingGuideStorage, guideActions } from "./storage";

/**
 * Hook to manage onboarding guide location and state.
 * Provides actions to move guides between dashboard and sidebar.
 *
 * State is automatically persisted to localStorage and reactive across tabs.
 */
export const useOnboardingGuide = () => {
  const { currentUser } = useCurrentUser();
  const { isCollapsed: isSidebarCollapsed, toggleCollapseClass } =
    useSidebarContainer();
  const userId = currentUser?.id?.toString() || "";

  const [guideState, setGuideState] = useOnboardingGuideStorage(userId);

  // Automatically move to dashboard when remind me later period expires
  useEffect(() => {
    if (
      guideState.guideLocation === "sidebar" &&
      guideState.remindMeLaterUntil
    ) {
      const hiddenUntil = new Date(guideState.remindMeLaterUntil);
      const now = new Date();

      if (now >= hiddenUntil) {
        // Timer expired, move to dashboard
        setGuideState((prev) => guideActions.moveToDashboard(prev));
      }
    }
  }, [guideState.guideLocation, guideState.remindMeLaterUntil, setGuideState]);

  const moveToSidebar = useCallback(
    (remindMeLaterDays?: number) => {
      setGuideState((prev) =>
        guideActions.moveToSidebar(prev, remindMeLaterDays)
      );

      // If sidebar is collapsed, expand it so the guide is visible
      if (isSidebarCollapsed) {
        toggleCollapseClass();
      }
    },
    [setGuideState, isSidebarCollapsed, toggleCollapseClass]
  );

  const moveToDashboard = useCallback(() => {
    // If not on dashboard, navigate there first
    if (window.location.pathname !== "/") {
      window.location.href = "/";
    }

    setGuideState((prev) => guideActions.moveToDashboard(prev));
  }, [setGuideState]);

  const shouldShowInSidebar = guideActions.shouldShowInSidebar(guideState);
  const shouldShowInDashboard = guideActions.shouldShowInDashboard(guideState);

  return useObjectMemo({
    guideLocation: guideState.guideLocation,
    remindMeLaterUntil: guideState.remindMeLaterUntil,
    shouldShowInSidebar,
    shouldShowInDashboard,
    moveToSidebar,
    moveToDashboard,
  });
};

/**
 * Sets a flag in localStorage to refresh guides state
 */
export const useTriggerGuideForceReset = () => {
  const { currentUser } = useCurrentUser();
  const userId = currentUser?.id?.toString() || "";
  const [, setGuideState] = useOnboardingGuideStorage(userId);

  return useCallback(() => {
    setGuideState((prev) => guideActions.setForceReset(prev));
  }, [setGuideState]);
};
